<template>
  <header class="main-header">
    <div class="container-fluid">
      <div class="main-header__left-side">
        <app-menu-left @localeChange="onLocaleChange" :locale="locale" />
        <app-menu-icon
          class="main-header__mobile-trigger"
          @localeChange="onLocaleChange"
          @click="toggleMenu"
        />

        <transition name="fade">
          <div @click="toggleSearch" class="main-header__search-icon">
            <app-search-icon />
          </div>
        </transition>
      </div>

      <div class="main-header__logo">
        <b-link :href="$websitePath('/')">
          <app-logo-icon />
        </b-link>
      </div>

      <div class="main-header__right-side">
        <div class="main-header__ticket-validation">
          <app-ticket-validation-icon
            :href="$websitePath('/account/tickets#/')"
            class="main-header__ticket-validation-icon"
          />
        </div>
        <div class="main-header__search" @click="toggleSearch">
          <app-search-icon />
        </div>
        <div class="main-header__account">
          <app-user-icon @click="toggleProfile" />
          <app-dropdown
            v-model="showProfile"
            class="main-header__profile-dropdown"
            :transition-name="customer ? 'fade' : 'slide-left'"
            :class="{
              'main-header__profile-dropdown--logged-in': customer,
            }"
          >
            <app-account
              v-if="customer"
              :full-name="customer.fullName"
              :avatar-url="customer.avatarUrl"
              @logout="onLogout"
              @close="toggleProfile"
            />
            <app-login
              v-else
              :last-username="lastUsername"
              @input="onLogin"
              :error="loginErrorMessage"
              :loading="loginSubmitted"
              @close="toggleProfile"
            />
          </app-dropdown>
        </div>

        <a
          class="main-header__wishlist"
          :href="$websitePath('/contul-meu/wishlist/')"
        >
          <app-favorite-icon />
        </a>

        <div class="main-header__cart">
          <app-cart-icon
            class="main-header__cart-icon"
            @click="toggleCart"
            :count="cartTotalQuantity"
          />
          <app-dropdown v-model="showCart" class="main-header__cart__dropdown">
            <app-cart
              @close="toggleCart"
              :items="cartItems"
              :total="cartTotal"
            />
          </app-dropdown>
        </div>
      </div>
    </div>

    <div
      class="main-header__nav__overlay main-header__nav__overlay--cart"
      :class="{
        active: showCart,
      }"
      @click="toggleCart"
    ></div>

    <div
      class="main-header__nav__overlay main-header__nav__overlay--account"
      :class="{
        active: showProfile,
      }"
      @click="toggleProfile"
    ></div>

    <div
      class="main-header__nav__overlay main-header__nav__overlay--search"
      :class="{
        active: showSearch,
      }"
      @click="toggleSearch"
    ></div>

    <div
      class="main-header__nav__overlay"
      :class="{
        active: showMenu,
      }"
      @click="toggleMenu"
    ></div>

    <app-menu
      :class="{
        active: showMenu,
        'active-search': showSearch,
      }"
      :active-lang="showLang"
      :active-dropdown="showMenu"
      :mega-menu-items="megaMenu"
      :locale="locale"
      @langShow="toggleLang"
      @close="toggleMenu"
      @closeSubMenu="toggleOffAll"
      @openSubMenu="openSubMenu"
      @localeChange="onLocaleChange"
    />
    <transition name="slide-left">
      <app-search-bar
        v-if="showSearch"
        @close="toggleSearch"
        @input="onSearch"
      />
    </transition>
  </header>
</template>

<script>
import AppMenu from "@/components/layout/header/Menu.vue";
import AppMenuIcon from "@/components/layout/header/MenuIcon";
import AppMenuLeft from "@/components/layout/header/MenuLeft";
import AppSearchIcon from "@/components/ui/Icons/SearchIcon";
import AppSearchBar from "@/components/layout/header/SearchBar";
import AppDropdown from "@/components/layout/header/Dropdown";
import AppLogin from "@/components/layout/header/Login";
import AppAccount from "@/components/layout/header/Account";
import AppCartIcon from "@/components/layout/header/CartIcon";
import AppTicketValidationIcon from "@/components/layout/header/TicketValidationIcon";
import AppCart from "@/components/layout/header/Cart";
import { mapGetters, mapActions } from "vuex";
import { createValidationError } from "@/utils/validation";
import AppUserIcon from "@/components/ui/Icons/UserIcon";
import AppFavoriteIcon from "@/components/ui/Icons/FavoriteIcon";
import AppLogoIcon from "@/components/ui/Icons/LogoIcon";

export default {
  name: "AppHeader",
  components: {
    AppLogoIcon,
    AppFavoriteIcon,
    AppUserIcon,
    AppCart,
    AppCartIcon,
    AppTicketValidationIcon,
    AppAccount,
    AppLogin,
    AppDropdown,
    AppSearchBar,
    AppSearchIcon,
    AppMenuIcon,
    AppMenuLeft,
    AppMenu,
  },
  mounted() {
    this.$el.style["right"] = this.getBodyPaddingRight();
    this.bodyObserver = new MutationObserver(() => {
      this.$el.style["right"] = this.getBodyPaddingRight();
    });

    this.bodyObserver.observe(document.body, {
      attributes: true,
    });

    window.addEventListener("scroll", this.headroomScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.headroomScroll);
  },
  computed: {
    ...mapGetters("menus", ["megaMenu"]),
    ...mapGetters(["locale"]),
    ...mapGetters("security", ["lastUsername"]),
    ...mapGetters("cart", ["cart"]),
    ...mapGetters("customers", ["customer", "fullName", "avatarUrl"]),
    cartItems() {
      return this.cart.items;
    },
    cartTotal() {
      return this.cart.total || 0;
    },
    cartTotalQuantity() {
      return this.cart.items.reduce((total, item) => total + item.quantity, 0);
    },
  },
  watch: {
    $route() {
      this.toggleOffAll();
    },
  },
  methods: {
    ...mapActions("security", ["logout", "loginByUsername"]),
    ...mapActions(["changeLocale"]),
    getBodyPaddingRight() {
      return document.body.style["padding-right"] || 0;
    },
    getBodyPaddingTop() {
      return parseInt(
        window
          .getComputedStyle(document.body, null)
          .getPropertyValue("padding-top")
      );
    },
    toggleOpenedItemBodyClass(open) {
      if (document.body.classList.contains("freeze-page-scroll") && !open) {
        document.body.classList.remove("freeze-page-scroll");
      } else if (
        !document.body.classList.contains("freeze-page-scroll") &&
        open
      ) {
        document.body.classList.add("freeze-page-scroll");
      }
    },
    toggleOffAll() {
      this.showProfile = false;
      this.showSearch = false;
      this.showCart = false;
      this.showMenu = false;
      this.showLang = false;
      document.body.classList.remove("freeze-page-scroll");
    },
    toggleLang() {
      this.toggleOpenedItemBodyClass(!this.showLang);
      this.showProfile = false;
      this.showSearch = false;
      this.showCart = false;
      this.showMenu = false;
      this.showLang = !this.showLang;
    },
    toggleMenu() {
      this.toggleOpenedItemBodyClass(!this.showMenu);
      this.showProfile = false;
      this.showSearch = false;
      this.showCart = false;
      this.showLang = false;
      this.showMenu = !this.showMenu;
    },
    openSubMenu() {
      this.showProfile = false;
      this.showSearch = false;
      this.showCart = false;
      this.showLang = false;
      this.showMenu = true;
    },
    toggleSearch() {
      this.toggleOpenedItemBodyClass(!this.showSearch);
      this.showMenu = false;
      this.showProfile = false;
      this.showCart = false;
      this.showLang = false;
      this.showSearch = !this.showSearch;
    },
    toggleCart() {
      this.toggleOpenedItemBodyClass(!this.showCart);
      this.showMenu = false;
      this.showProfile = false;
      this.showSearch = false;
      this.showLang = false;
      this.showCart = !this.showCart;
    },
    toggleProfile() {
      this.toggleOpenedItemBodyClass(!this.showProfile);
      this.showMenu = false;
      this.showSearch = false;
      this.showCart = false;
      this.showLang = false;
      this.showProfile = !this.showProfile;
    },
    onLogout() {
      this.toggleOffAll();
      this.$router.push({ name: "login" });
      this.$auth.logout();
    },
    onSearch(query) {
      window.location = this.$websitePath(`/search?term=${query}`);
    },
    async onLogin(form) {
      this.loginSubmitted = true;
      this.loginErrorMessage = null;

      try {
        await this.loginByUsername(form);
        this.toggleOffAll();
        this.$router.push({ name: "my-plane" });
      } catch (e) {
        const ve = createValidationError(e);

        this.loginErrorMessage = ve.errors.message;
      } finally {
        this.loginSubmitted = false;
      }
    },
    onLocaleChange(locale) {
      this.changeLocale(locale);
    },
    headroomScroll() {
      if (window.scrollY > this.getBodyPaddingTop() * 2) {
        if (this.oldScroll > window.scrollY) {
          document.body.classList.remove("hide-header");
        } else {
          this.toggleOffAll();
          document.body.classList.add("hide-header");
        }
      } else {
        document.body.classList.remove("hide-header");
      }
      this.oldScroll = window.scrollY;
    },
  },
  data() {
    return {
      loginSubmitted: false,
      showMenu: false,
      showProfile: false,
      showSearch: false,
      showCart: false,
      showLang: false,
      menuTransition: "slide-left-to-right",
      loginErrorMessage: null,
      oldScroll: 0,
    };
  },
};
</script>
